import { useReducer } from 'react';
import { fNumber } from '../../../utils/helpers';
import { useCarStoreContext } from '../../components/fleet-calculator/store/car-store-provider';
import useSettingsStore from '../../components/fleet-calculator/store/settings-store';
import { TypeOfLeasing } from '../../components/fleet-calculator/types';
import { priceTable } from './copy';

const PriceTable = () => {
  const activeCarPair = useCarStoreContext((state) => state.activeCarPair);

  const { typeOfLeasing } = useSettingsStore();

  const reducer = (
    state: { firstOpen: boolean; secondOpen: boolean },
    action: { type: 'firstToggle' | 'secondToggle' },
  ) => {
    switch (action.type) {
      case 'firstToggle':
        return { ...state, firstOpen: !state.firstOpen };
      case 'secondToggle':
        return { ...state, secondOpen: !state.secondOpen };
      default:
        return state;
    }
  };

  const [tables, dispatch] = useReducer(reducer, {
    firstOpen: false,
    secondOpen: false,
  });

  if (!activeCarPair || activeCarPair.length === 0) {
    return <div className="price-table"></div>;
  }

  const driverCostRows = [
    {
      label: priceTable.formansVarde,
      value: (calc) => calc.nettoFormansVarde.formansvarde_netto_monthly,
    },
    {
      label: priceTable.formansVardeFuel,
      value: (calc) =>
        calc.nettoFormansVarde.formansvarde_bransle_netto_monthly,
    },
    { label: priceTable.fuel, value: (calc) => calc.fuelCostMonthlyDriver },
  ];

  const companyCostRows = [
    { label: priceTable.finance, value: (calc) => calc.monthlyCost },
    {
      label: priceTable.serviceDeal,
      value: (calc) => calc.serviceCost,
      businessOnly: true,
    },
    {
      label: priceTable.insurance,
      value: (calc) => calc.insuranceCost,
      businessOnly: true,
    },
    { label: priceTable.carTax, value: (calc) => calc.taxMonthly },
    {
      label: priceTable.fuelCost,
      value: (calc) => calc.fuelCostMonthlyBusiness,
    },
    { label: priceTable.socialCost, value: (calc) => calc.socialCostMonthly },
    {
      label: priceTable.socialCostFuel,
      value: (calc) => calc.socialCostFuelMonthly,
    },
  ];

  return (
    <div className="w-full col-span-2 min-w-0 font-sans space-y-6 text-gray-900">
      <div>
        <div className="grid">
          <p className="text-sm text-dark-blue font-semibold">
            {priceTable.formansVardeNetto}
          </p>
          <p className="text-xs text-dark-blue">Från ca</p>
          {activeCarPair.slice(1).map((car) => (
            <div key={car.car.id}></div>
          ))}
        </div>
        <div className="grid mt-2 grid-cols-2 gap-4 lg:gap-8">
          {activeCarPair.map((carData) => (
            <p key={carData.car.id} className="text-2xl tracking-tight">
              {fNumber(
                carData.calc.nettoFormansVarde.formansvarde_netto_monthly,
              )}
            </p>
          ))}
        </div>
      </div>

      <div className="border-t border-kia-grey pt-4">
        <div className="grid items-start grid-cols-2 gap-x-4 lg:gap-x-8">
          <div>
            <p className="text-sm font-semibold">{priceTable.totalUser}</p>
            <p className="text-xs text-dark-blue">{priceTable.inclFuel}</p>
          </div>
          <button
            onClick={() => dispatch({ type: 'firstToggle' })}
            className="text-2xl justify-self-end text-gray-400 size-8 hover:bg-gray-100 rounded-full flex items-center justify-center"
          >
            {tables.firstOpen ? '−' : '＋'}
          </button>
          {activeCarPair.map((carData) => (
            <p key={carData.car.id} className="text-2xl tracking-tight mt-2">
              {fNumber(carData.calc.totalCostPersonal)}
            </p>
          ))}
        </div>

        {tables.firstOpen && (
          <ul className="mt-4 divide-y divide-kia-grey">
            {driverCostRows.map((row) => (
              <li
                key={row.label}
                className="grid text-sm grid-cols-2 gap-x-4 lg:gap-x-8 py-2"
              >
                {activeCarPair.map((carData) => (
                  <div key={carData.car.id}>
                    <p className="text-dark-blue text-sm">{row.label}</p>
                    <p className="tabular-nums">
                      {fNumber(row.value(carData.calc))}
                    </p>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-kia-grey pt-4">
        <div className="grid items-start grid-cols-2 gap-x-4 lg:gap-x-8">
          <div>
            <p className="text-sm font-semibold">{priceTable.totalCompany}</p>
            <p className="text-xs text-dark-blue">Exkl. moms</p>
          </div>
          <button
            onClick={() => dispatch({ type: 'secondToggle' })}
            className="text-2xl justify-self-end text-gray-400 size-8 hover:bg-gray-100 rounded-full grid place-content-center"
          >
            {tables.secondOpen ? '−' : '＋'}
          </button>
          {activeCarPair.map((carData) => (
            <p key={carData.car.id} className="text-2xl tracking-tight mt-2">
              {fNumber(carData.calc.totalCostBusiness)}
            </p>
          ))}
          <div className="flex justify-end"></div>
        </div>

        {tables.secondOpen && (
          <ul className="mt-4 border-gray-200 divide-y divide-kia-grey">
            {companyCostRows.map((row) => (
              <li
                key={row.label}
                className="grid text-sm grid-cols-2 gap-x-4 lg:gap-x-8 py-2"
              >
                {activeCarPair.map((carData) => (
                  <div key={carData.car.id}>
                    <p className="text-dark-blue text-sm">{row.label}</p>
                    <p className="tabular-nums">
                      {row.businessOnly &&
                      typeOfLeasing === TypeOfLeasing.BUSINESS
                        ? priceTable.included
                        : `${fNumber(row.value(carData.calc))}`}
                    </p>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PriceTable;
